import {Injectable} from '@angular/core';
import {Helper} from '../../helpers/helper.service';
import {HttpClient, HttpParams} from '@angular/common/http';
import {environment} from '../../../environments/environment';
import {BehaviorSubject} from 'rxjs';
import {Solution} from '../Models/Solution';
import {ActionType} from '../Models/ActionType';
import {TranslateService} from '@ngx-translate/core';
import {SolutionStep} from '../Models/SolutionStep';
import { catchError } from 'rxjs/operators';

@Injectable()
export class SolutionService {
  public solution: BehaviorSubject<Solution> = new BehaviorSubject<Solution>(new Solution());
  public actionTypes: ActionType[] = [];

  constructor(
    private helper: Helper,
    private http: HttpClient,
    private translate: TranslateService
  ) {
  }

  public getSolutions(gridData = new HttpParams()) {
    return this.http.get<any>(environment.apiUrl + '/backoffice/solution/?' + gridData.toString())
      .pipe(catchError(res => this.helper.catchError(res)));
  }

  public getArchiveSolutions(gridData = new HttpParams()) {
    return this.http.get<any>(environment.apiUrl + '/backoffice/solution/archive/?' + gridData.toString())
      .pipe(catchError(res => this.helper.catchError(res)));
  }

  public setPosition(position, id = null) {
    return this.http.put(environment.apiUrl + '/backoffice/solution/' + id + '/change-position', position)
      .pipe(catchError(res => this.helper.catchError(res)));
  }


  public deleteSolution(id) {
    return this.http.delete(environment.apiUrl + '/backoffice/solution/' + id)
      .pipe(catchError(res => this.helper.catchError(res)));
  }

  public readdSolution(id) {
    return this.http.put(environment.apiUrl + '/backoffice/solution/' + id + '/readd', {id: id})
      .pipe(catchError(res => this.helper.catchError(res)));
  }

  public getSolution(solutionId) {
    return this.http.get<any>(
      environment.apiUrl + '/backoffice/solution/' + solutionId
    ).pipe(catchError(res => this.helper.catchError(res)));
  }

  public fetchSolutionComplete(solutionId) {
    this.solution.next(new Solution());
    return this.getActionTypes().then(() => this.http.get<Solution>(
        environment.apiUrl + '/backoffice/solution/' + solutionId + '/more'
      ).pipe(catchError(res => this.helper.catchError(res))).subscribe((res: Solution) => {
        res.solutionSteps.forEach((step, index) => {
          res.solutionSteps[index].actionType = (typeof step.actionType === 'object') ? step.actionType.id : step.actionType;
          if (step.nextStep) {
            res.solutionSteps[index].nextStep = res.solutionSteps.findIndex(s => s.id === (<SolutionStep>step.nextStep).id);
          } else {
            res.solutionSteps[index].nextStep = -1;
          }
          if (step.actionConfirm) {
            if (step.actionConfirm.primaryStep) {
              res.solutionSteps[index].actionConfirm.primaryStep =
                res.solutionSteps.findIndex(s => s.id === (<SolutionStep>step.actionConfirm.primaryStep).id);
            } else {
              res.solutionSteps[index].actionConfirm.primaryStep = -1;
            }
            if (step.actionConfirm.secondaryStep) {
              res.solutionSteps[index].actionConfirm.secondaryStep =
                res.solutionSteps.findIndex(s => s.id === (<SolutionStep>step.actionConfirm.secondaryStep).id);
            } else {
              res.solutionSteps[index].actionConfirm.secondaryStep = -1;
            }
          }
        });
        this.solution.next(res);
      }));
  }

  public getActionTypes(): Promise<ActionType[]> {
    return new Promise<ActionType[]>((resolve) => {
      if (this.actionTypes.length) {
        return resolve(this.actionTypes.map(type => {
          type.label = this.translate.instant(type.translationKey);
          return type;
        }));
      }
      this.http.get<any>(
        environment.apiUrl + '/backoffice/solution/action-type/list'
      ).pipe(catchError(res => this.helper.catchError(res))).subscribe(res => {
        resolve(this.actionTypes = res.data.map(type => {
          type.label = this.translate.instant(type.translationKey);
          return type;
        }));
      });
    });
  }

  public manageSolution(serviceSubject, isEdit, id = null) {
    if (isEdit) {
      return this.http.put(
        environment.apiUrl + '/backoffice/solution/' + id,
        serviceSubject
      ).pipe(catchError(res => this.helper.catchError(res)));
    }
    return this.http.post(
      environment.apiUrl + '/backoffice/solution/ ',
      serviceSubject
    ).pipe(catchError(res => this.helper.catchError(res)));
  }

  public manageSolutionSteps(solution: Solution) {
    return this.http.post(
      environment.apiUrl + '/backoffice/solution/solution-step',
      {solution: solution}
    ).pipe(catchError(res => this.helper.catchError(res)));
  }
}
